"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createDefaultStyle = createDefaultStyle;
exports.createEditingStyle = createEditingStyle;
exports["default"] = void 0;
exports.toFunction = toFunction;

var _Circle = _interopRequireDefault(require("./Circle.js"));

var _Fill = _interopRequireDefault(require("./Fill.js"));

var _GeometryType = _interopRequireDefault(require("../geom/GeometryType.js"));

var _Stroke = _interopRequireDefault(require("./Stroke.js"));

var _asserts = require("../asserts.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

/**
 * A function that takes an {@link module:ol/Feature~Feature} and a `{number}`
 * representing the view's resolution. The function should return a
 * {@link module:ol/style/Style~Style} or an array of them. This way e.g. a
 * vector layer can be styled. If the function returns `undefined`, the
 * feature will not be rendered.
 *
 * @typedef {function(import("../Feature.js").FeatureLike, number):(Style|Array<Style>|void)} StyleFunction
 */

/**
 * A {@link Style}, an array of {@link Style}, or a {@link StyleFunction}.
 * @typedef {Style|Array<Style>|StyleFunction} StyleLike
 */

/**
 * A function that takes an {@link module:ol/Feature~Feature} as argument and returns an
 * {@link module:ol/geom/Geometry~Geometry} that will be rendered and styled for the feature.
 *
 * @typedef {function(import("../Feature.js").FeatureLike):
 *     (import("../geom/Geometry.js").default|import("../render/Feature.js").default|undefined)} GeometryFunction
 */

/**
 * Custom renderer function. Takes two arguments:
 *
 * 1. The pixel coordinates of the geometry in GeoJSON notation.
 * 2. The {@link module:ol/render~State} of the layer renderer.
 *
 * @typedef {function((import("../coordinate.js").Coordinate|Array<import("../coordinate.js").Coordinate>|Array<Array<import("../coordinate.js").Coordinate>>),import("../render.js").State): void}
 * RenderFunction
 */

/**
 * @typedef {Object} Options
 * @property {string|import("../geom/Geometry.js").default|GeometryFunction} [geometry] Feature property or geometry
 * or function returning a geometry to render for this style.
 * @property {import("./Fill.js").default} [fill] Fill style.
 * @property {import("./Image.js").default} [image] Image style.
 * @property {RenderFunction} [renderer] Custom renderer. When configured, `fill`, `stroke` and `image` will be
 * ignored, and the provided function will be called with each render frame for each geometry.
 * @property {RenderFunction} [hitDetectionRenderer] Custom renderer for hit detection. If provided will be used
 * in hit detection rendering.
 * @property {import("./Stroke.js").default} [stroke] Stroke style.
 * @property {import("./Text.js").default} [text] Text style.
 * @property {number} [zIndex] Z index.
 */

/**
 * @classdesc
 * Container for vector feature rendering styles. Any changes made to the style
 * or its children through `set*()` methods will not take effect until the
 * feature or layer that uses the style is re-rendered.
 *
 * ## Feature styles
 *
 * If no style is defined, the following default style is used:
 * ```js
 *  import {Circle, Fill, Stroke, Style} from 'ol/style';
 *
 *  const fill = new Fill({
 *    color: 'rgba(255,255,255,0.4)',
 *  });
 *  const stroke = new Stroke({
 *    color: '#3399CC',
 *    width: 1.25,
 *  });
 *  const styles = [
 *    new Style({
 *      image: new Circle({
 *        fill: fill,
 *        stroke: stroke,
 *        radius: 5,
 *      }),
 *      fill: fill,
 *      stroke: stroke,
 *    }),
 *  ];
 * ```
 *
 * A separate editing style has the following defaults:
 * ```js
 *  import GeometryType from 'ol/geom/GeometryType';
 *  import {Circle, Fill, Stroke, Style} from 'ol/style';
 *
 *  const styles = {};
 *  const white = [255, 255, 255, 1];
 *  const blue = [0, 153, 255, 1];
 *  const width = 3;
 *  styles[GeometryType.POLYGON] = [
 *    new Style({
 *      fill: new Fill({
 *        color: [255, 255, 255, 0.5],
 *      }),
 *    }),
 *  ];
 *  styles[GeometryType.MULTI_POLYGON] = styles[GeometryType.POLYGON];
 *
 *  styles[GeometryType.LINE_STRING] = [
 *    new Style({
 *      stroke: new Stroke({
 *        color: white,
 *        width: width + 2,
 *      }),
 *    }),
 *    new Style({
 *      stroke: new Stroke({
 *        color: blue,
 *        width: width,
 *      }),
 *    }),
 *  ];
 *  styles[GeometryType.MULTI_LINE_STRING] = styles[GeometryType.LINE_STRING];
 *
 *  styles[GeometryType.CIRCLE] = styles[GeometryType.POLYGON].concat(
 *    styles[GeometryType.LINE_STRING]
 *  );
 *
 *  styles[GeometryType.POINT] = [
 *    new Style({
 *      image: new Circle({
 *        radius: width * 2,
 *        fill: new Fill({
 *          color: blue,
 *        }),
 *        stroke: new Stroke({
 *          color: white,
 *          width: width / 2,
 *        }),
 *      }),
 *      zIndex: Infinity,
 *    }),
 *  ];
 *  styles[GeometryType.MULTI_POINT] = styles[GeometryType.POINT];
 *
 *  styles[GeometryType.GEOMETRY_COLLECTION] = styles[
 *    GeometryType.POLYGON
 *  ].concat(styles[GeometryType.LINE_STRING], styles[GeometryType.POINT]);
 * ```
 *
 * @api
 */
var Style =
/** @class */
function () {
  /**
   * @param {Options} [opt_options] Style options.
   */
  function Style(opt_options) {
    var options = opt_options || {};
    /**
     * @private
     * @type {string|import("../geom/Geometry.js").default|GeometryFunction}
     */

    this.geometry_ = null;
    /**
     * @private
     * @type {!GeometryFunction}
     */

    this.geometryFunction_ = defaultGeometryFunction;

    if (options.geometry !== undefined) {
      this.setGeometry(options.geometry);
    }
    /**
     * @private
     * @type {import("./Fill.js").default}
     */


    this.fill_ = options.fill !== undefined ? options.fill : null;
    /**
     * @private
     * @type {import("./Image.js").default}
     */

    this.image_ = options.image !== undefined ? options.image : null;
    /**
     * @private
     * @type {RenderFunction|null}
     */

    this.renderer_ = options.renderer !== undefined ? options.renderer : null;
    /**
     * @private
     * @type {RenderFunction|null}
     */

    this.hitDetectionRenderer_ = options.hitDetectionRenderer !== undefined ? options.hitDetectionRenderer : null;
    /**
     * @private
     * @type {import("./Stroke.js").default}
     */

    this.stroke_ = options.stroke !== undefined ? options.stroke : null;
    /**
     * @private
     * @type {import("./Text.js").default}
     */

    this.text_ = options.text !== undefined ? options.text : null;
    /**
     * @private
     * @type {number|undefined}
     */

    this.zIndex_ = options.zIndex;
  }
  /**
   * Clones the style.
   * @return {Style} The cloned style.
   * @api
   */


  Style.prototype.clone = function () {
    var geometry = this.getGeometry();

    if (geometry && _typeof(geometry) === 'object') {
      geometry =
      /** @type {import("../geom/Geometry.js").default} */
      geometry.clone();
    }

    return new Style({
      geometry: geometry,
      fill: this.getFill() ? this.getFill().clone() : undefined,
      image: this.getImage() ? this.getImage().clone() : undefined,
      renderer: this.getRenderer(),
      stroke: this.getStroke() ? this.getStroke().clone() : undefined,
      text: this.getText() ? this.getText().clone() : undefined,
      zIndex: this.getZIndex()
    });
  };
  /**
   * Get the custom renderer function that was configured with
   * {@link #setRenderer} or the `renderer` constructor option.
   * @return {RenderFunction|null} Custom renderer function.
   * @api
   */


  Style.prototype.getRenderer = function () {
    return this.renderer_;
  };
  /**
   * Sets a custom renderer function for this style. When set, `fill`, `stroke`
   * and `image` options of the style will be ignored.
   * @param {RenderFunction|null} renderer Custom renderer function.
   * @api
   */


  Style.prototype.setRenderer = function (renderer) {
    this.renderer_ = renderer;
  };
  /**
   * Sets a custom renderer function for this style used
   * in hit detection.
   * @param {RenderFunction|null} renderer Custom renderer function.
   * @api
   */


  Style.prototype.setHitDetectionRenderer = function (renderer) {
    this.hitDetectionRenderer_ = renderer;
  };
  /**
   * Get the custom renderer function that was configured with
   * {@link #setHitDetectionRenderer} or the `hitDetectionRenderer` constructor option.
   * @return {RenderFunction|null} Custom renderer function.
   * @api
   */


  Style.prototype.getHitDetectionRenderer = function () {
    return this.hitDetectionRenderer_;
  };
  /**
   * Get the geometry to be rendered.
   * @return {string|import("../geom/Geometry.js").default|GeometryFunction}
   * Feature property or geometry or function that returns the geometry that will
   * be rendered with this style.
   * @api
   */


  Style.prototype.getGeometry = function () {
    return this.geometry_;
  };
  /**
   * Get the function used to generate a geometry for rendering.
   * @return {!GeometryFunction} Function that is called with a feature
   * and returns the geometry to render instead of the feature's geometry.
   * @api
   */


  Style.prototype.getGeometryFunction = function () {
    return this.geometryFunction_;
  };
  /**
   * Get the fill style.
   * @return {import("./Fill.js").default} Fill style.
   * @api
   */


  Style.prototype.getFill = function () {
    return this.fill_;
  };
  /**
   * Set the fill style.
   * @param {import("./Fill.js").default} fill Fill style.
   * @api
   */


  Style.prototype.setFill = function (fill) {
    this.fill_ = fill;
  };
  /**
   * Get the image style.
   * @return {import("./Image.js").default} Image style.
   * @api
   */


  Style.prototype.getImage = function () {
    return this.image_;
  };
  /**
   * Set the image style.
   * @param {import("./Image.js").default} image Image style.
   * @api
   */


  Style.prototype.setImage = function (image) {
    this.image_ = image;
  };
  /**
   * Get the stroke style.
   * @return {import("./Stroke.js").default} Stroke style.
   * @api
   */


  Style.prototype.getStroke = function () {
    return this.stroke_;
  };
  /**
   * Set the stroke style.
   * @param {import("./Stroke.js").default} stroke Stroke style.
   * @api
   */


  Style.prototype.setStroke = function (stroke) {
    this.stroke_ = stroke;
  };
  /**
   * Get the text style.
   * @return {import("./Text.js").default} Text style.
   * @api
   */


  Style.prototype.getText = function () {
    return this.text_;
  };
  /**
   * Set the text style.
   * @param {import("./Text.js").default} text Text style.
   * @api
   */


  Style.prototype.setText = function (text) {
    this.text_ = text;
  };
  /**
   * Get the z-index for the style.
   * @return {number|undefined} ZIndex.
   * @api
   */


  Style.prototype.getZIndex = function () {
    return this.zIndex_;
  };
  /**
   * Set a geometry that is rendered instead of the feature's geometry.
   *
   * @param {string|import("../geom/Geometry.js").default|GeometryFunction} geometry
   *     Feature property or geometry or function returning a geometry to render
   *     for this style.
   * @api
   */


  Style.prototype.setGeometry = function (geometry) {
    if (typeof geometry === 'function') {
      this.geometryFunction_ = geometry;
    } else if (typeof geometry === 'string') {
      this.geometryFunction_ = function (feature) {
        return (
          /** @type {import("../geom/Geometry.js").default} */
          feature.get(geometry)
        );
      };
    } else if (!geometry) {
      this.geometryFunction_ = defaultGeometryFunction;
    } else if (geometry !== undefined) {
      this.geometryFunction_ = function () {
        return (
          /** @type {import("../geom/Geometry.js").default} */
          geometry
        );
      };
    }

    this.geometry_ = geometry;
  };
  /**
   * Set the z-index.
   *
   * @param {number|undefined} zIndex ZIndex.
   * @api
   */


  Style.prototype.setZIndex = function (zIndex) {
    this.zIndex_ = zIndex;
  };

  return Style;
}();
/**
 * Convert the provided object into a style function.  Functions passed through
 * unchanged.  Arrays of Style or single style objects wrapped in a
 * new style function.
 * @param {StyleFunction|Array<Style>|Style} obj
 *     A style function, a single style, or an array of styles.
 * @return {StyleFunction} A style function.
 */


function toFunction(obj) {
  var styleFunction;

  if (typeof obj === 'function') {
    styleFunction = obj;
  } else {
    /**
     * @type {Array<Style>}
     */
    var styles_1;

    if (Array.isArray(obj)) {
      styles_1 = obj;
    } else {
      (0, _asserts.assert)(typeof
      /** @type {?} */
      obj.getZIndex === 'function', 41); // Expected an `Style` or an array of `Style`

      var style =
      /** @type {Style} */
      obj;
      styles_1 = [style];
    }

    styleFunction = function styleFunction() {
      return styles_1;
    };
  }

  return styleFunction;
}
/**
 * @type {Array<Style>|null}
 */


var defaultStyles = null;
/**
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {number} resolution Resolution.
 * @return {Array<Style>} Style.
 */

function createDefaultStyle(feature, resolution) {
  // We don't use an immediately-invoked function
  // and a closure so we don't get an error at script evaluation time in
  // browsers that do not support Canvas. (import("./Circle.js").CircleStyle does
  // canvas.getContext('2d') at construction time, which will cause an.error
  // in such browsers.)
  if (!defaultStyles) {
    var fill = new _Fill["default"]({
      color: 'rgba(255,255,255,0.4)'
    });
    var stroke = new _Stroke["default"]({
      color: '#3399CC',
      width: 1.25
    });
    defaultStyles = [new Style({
      image: new _Circle["default"]({
        fill: fill,
        stroke: stroke,
        radius: 5
      }),
      fill: fill,
      stroke: stroke
    })];
  }

  return defaultStyles;
}
/**
 * Default styles for editing features.
 * @return {Object<import("../geom/GeometryType.js").default, Array<Style>>} Styles
 */


function createEditingStyle() {
  /** @type {Object<import("../geom/GeometryType.js").default, Array<Style>>} */
  var styles = {};
  var white = [255, 255, 255, 1];
  var blue = [0, 153, 255, 1];
  var width = 3;
  styles[_GeometryType["default"].POLYGON] = [new Style({
    fill: new _Fill["default"]({
      color: [255, 255, 255, 0.5]
    })
  })];
  styles[_GeometryType["default"].MULTI_POLYGON] = styles[_GeometryType["default"].POLYGON];
  styles[_GeometryType["default"].LINE_STRING] = [new Style({
    stroke: new _Stroke["default"]({
      color: white,
      width: width + 2
    })
  }), new Style({
    stroke: new _Stroke["default"]({
      color: blue,
      width: width
    })
  })];
  styles[_GeometryType["default"].MULTI_LINE_STRING] = styles[_GeometryType["default"].LINE_STRING];
  styles[_GeometryType["default"].CIRCLE] = styles[_GeometryType["default"].POLYGON].concat(styles[_GeometryType["default"].LINE_STRING]);
  styles[_GeometryType["default"].POINT] = [new Style({
    image: new _Circle["default"]({
      radius: width * 2,
      fill: new _Fill["default"]({
        color: blue
      }),
      stroke: new _Stroke["default"]({
        color: white,
        width: width / 2
      })
    }),
    zIndex: Infinity
  })];
  styles[_GeometryType["default"].MULTI_POINT] = styles[_GeometryType["default"].POINT];
  styles[_GeometryType["default"].GEOMETRY_COLLECTION] = styles[_GeometryType["default"].POLYGON].concat(styles[_GeometryType["default"].LINE_STRING], styles[_GeometryType["default"].POINT]);
  return styles;
}
/**
 * Function that is called with a feature and returns its default geometry.
 * @param {import("../Feature.js").FeatureLike} feature Feature to get the geometry for.
 * @return {import("../geom/Geometry.js").default|import("../render/Feature.js").default|undefined} Geometry to render.
 */


function defaultGeometryFunction(feature) {
  return feature.getGeometry();
}

var _default = Style;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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